/**
 * API Service Layer
 * Centralized API client for all backend endpoints
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_VERSION = '/api/v1';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Service
export const api = {
  // Health check
  health: {
    check: () => apiClient.get('/health'),
  },

  // Authentication
  auth: {
    login: (email: string, password: string) =>
      apiClient.post('/auth/login-json', { email, password }),
    register: (data: any) => apiClient.post('/auth/register', data),
    logout: () => apiClient.post('/auth/logout'),
    me: () => apiClient.get('/auth/me'),
    refreshToken: () => apiClient.post('/auth/refresh'),
  },

  // Supply Chain - Products
  products: {
    list: (params?: any) => apiClient.get('/supply-chain/products/', { params }),
    get: (id: string) => apiClient.get(`/supply-chain/products/${id}`),
    create: (data: any) => apiClient.post('/supply-chain/products/', data),
    update: (id: string, data: any) => apiClient.put(`/supply-chain/products/${id}`, data),
    delete: (id: string) => apiClient.delete(`/supply-chain/products/${id}`),
    export: (format: string) => apiClient.get(`/supply-chain/products/export?format=${format}`),
    import: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post('/supply-chain/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
  },

  // Supply Chain - Categories
  categories: {
    list: () => apiClient.get('/supply-chain/products/categories'),
    get: (id: string) => apiClient.get(`/supply-chain/products/categories/${id}`),
    create: (data: any) => apiClient.post('/supply-chain/products/categories', data),
    update: (id: string, data: any) => apiClient.put(`/supply-chain/products/categories/${id}`, data),
    delete: (id: string) => apiClient.delete(`/supply-chain/products/categories/${id}`),
  },

  // Supply Chain - Variants
  variants: {
    list: (productId: string) => apiClient.get(`/supply-chain/products/${productId}/variants`),
    get: (id: string) => apiClient.get(`/supply-chain/products/variants/${id}`),
    create: (productId: string, data: any) =>
      apiClient.post(`/supply-chain/products/${productId}/variants`, data),
    update: (id: string, data: any) => apiClient.put(`/supply-chain/products/variants/${id}`, data),
    delete: (id: string) => apiClient.delete(`/supply-chain/products/variants/${id}`),
  },

  // Simulation - Digital Twin
  simulation: {
    create: (config: any) => apiClient.post('/simulation/create', config),
    list: () => apiClient.get('/simulation/list'),
    get: (id: string) => apiClient.get(`/simulation/${id}`),
    run: (id: string, config: any) => apiClient.post(`/simulation/${id}/run`, config),
    stop: (id: string) => apiClient.post(`/simulation/${id}/stop`),
    results: (id: string) => apiClient.get(`/simulation/${id}/results`),
    optimize: (id: string, config: any) => apiClient.post(`/simulation/${id}/optimize`, config),
    predict: (id: string, config: any) => apiClient.post(`/simulation/${id}/predict`, config),
    scenario: (id: string, config: any) => apiClient.post(`/simulation/${id}/scenario`, config),
    topology: (id: string) => apiClient.get(`/simulation/${id}/topology`),
    addComponent: (id: string, type: string, component: any) =>
      apiClient.post(`/simulation/${id}/add/${type}`, component),
    updateComponent: (id: string, type: string, componentId: string, updates: any) =>
      apiClient.put(`/simulation/${id}/update/${type}/${componentId}`, updates),
    deleteComponent: (id: string, type: string, componentId: string) =>
      apiClient.delete(`/simulation/${id}/delete/${type}/${componentId}`),
  },

  // AI Chat
  chat: {
    sendMessage: (message: string, context?: any) =>
      apiClient.post('/chat/message', { message, context }),
    getHistory: (limit?: number) =>
      apiClient.get('/chat/history', { params: { limit } }),
    getSuggestions: () => apiClient.get('/chat/suggestions'),
  },

  // Global Search
  search: {
    query: (q: string, types?: string, limit?: number) =>
      apiClient.get('/search', { params: { q, types, limit } }),
    suggestions: (q: string) =>
      apiClient.get('/search/suggestions', { params: { q } }),
  },

  // Supply Chain - Suppliers
  suppliers: {
    list: (params?: any) => apiClient.get('/supply-chain/suppliers/', { params }),
    get: (id: string) => apiClient.get(`/supply-chain/suppliers/${id}`),
    create: (data: any) => apiClient.post('/supply-chain/suppliers/', data),
    update: (id: string, data: any) => apiClient.put(`/supply-chain/suppliers/${id}`, data),
    delete: (id: string) => apiClient.delete(`/supply-chain/suppliers/${id}`),
  },

  // Supply Chain - Inventory
  inventory: {
    list: (params?: any) => apiClient.get('/supply-chain/inventory/', { params }),
    get: (id: string) => apiClient.get(`/supply-chain/inventory/${id}`),
    create: (data: any) => apiClient.post('/supply-chain/inventory/', data),
    update: (id: string, data: any) => apiClient.put(`/supply-chain/inventory/${id}`, data),
    delete: (id: string) => apiClient.delete(`/supply-chain/inventory/${id}`),
  },

  // Supply Chain - Locations
  locations: {
    list: (params?: any) => apiClient.get('/supply-chain/locations/', { params }),
    get: (id: string) => apiClient.get(`/supply-chain/locations/${id}`),
    create: (data: any) => apiClient.post('/supply-chain/locations/', data),
    update: (id: string, data: any) => apiClient.put(`/supply-chain/locations/${id}`, data),
    delete: (id: string) => apiClient.delete(`/supply-chain/locations/${id}`),
  },

  // Supply Chain - Procurement
  procurement: {
    orders: {
      list: (params?: any) => apiClient.get('/supply-chain/procurement/orders', { params }),
      get: (id: string) => apiClient.get(`/supply-chain/procurement/orders/${id}`),
      create: (data: any) => apiClient.post('/supply-chain/procurement/orders', data),
      update: (id: string, data: any) => apiClient.put(`/supply-chain/procurement/orders/${id}`, data),
      delete: (id: string) => apiClient.delete(`/supply-chain/procurement/orders/${id}`),
    },
  },

  // Supply Chain - Receiving
  receiving: {
    receipts: {
      list: (params?: any) => apiClient.get('/supply-chain/receiving/receipts', { params }),
      get: (id: string) => apiClient.get(`/supply-chain/receiving/receipts/${id}`),
      create: (data: any) => apiClient.post('/supply-chain/receiving/receipts', data),
    },
  },

  // Supply Chain - Returns
  returns: {
    list: (params?: any) => apiClient.get('/supply-chain/returns/returns', { params }),
    get: (id: string) => apiClient.get(`/supply-chain/returns/returns/${id}`),
    create: (data: any) => apiClient.post('/supply-chain/returns/returns', data),
    update: (id: string, data: any) => apiClient.put(`/supply-chain/returns/returns/${id}`, data),
  },

  // Supply Chain - Reordering
  reordering: {
    suggestions: () => apiClient.get('/supply-chain/reordering/suggestions'),
    rules: {
      list: () => apiClient.get('/supply-chain/reordering/rules'),
      create: (data: any) => apiClient.post('/supply-chain/reordering/rules', data),
      delete: (id: string) => apiClient.delete(`/supply-chain/reordering/rules/${id}`),
    },
  },


  // Onboarding
  onboarding: {
    start: (data: any) => apiClient.post('/onboarding/start', data),
    getStatus: () => apiClient.get('/onboarding/status'),
    complete: (step: string) => apiClient.post('/onboarding/complete', { step }),
  },

  // Settings
  settings: {
    get: () => apiClient.get('/settings/'),
    update: (data: any) => apiClient.put('/settings/', data),
    getPreferences: () => apiClient.get('/settings/preferences'),
    updatePreferences: (data: any) => apiClient.put('/settings/preferences', data),
  },

  // Subscriptions
  subscriptions: {
    list: () => apiClient.get('/subscriptions/'),
    get: (id: string) => apiClient.get(`/subscriptions/${id}`),
    create: (data: any) => apiClient.post('/subscriptions/', data),
    update: (id: string, data: any) => apiClient.put(`/subscriptions/${id}`, data),
    cancel: (id: string) => apiClient.delete(`/subscriptions/${id}`),
  },

  // Feature Flags
  featureFlags: {
    list: () => apiClient.get('/feature-flags/'),
    get: (id: string) => apiClient.get(`/feature-flags/${id}`),
    create: (data: any) => apiClient.post('/feature-flags/', data),
    update: (id: string, data: any) => apiClient.put(`/feature-flags/${id}`, data),
    delete: (id: string) => apiClient.delete(`/feature-flags/${id}`),
  },

  // Usage Tracking
  usage: {
    getStats: () => apiClient.get('/usage/'),
    track: (event: string, data?: any) => apiClient.post('/usage/track', { event, ...data }),
  },

  // Companies
  companies: {
    list: () => apiClient.get('/companies/'),
    get: (id: string) => apiClient.get(`/companies/${id}`),
    create: (data: any) => apiClient.post('/companies/', data),
    update: (id: string, data: any) => apiClient.put(`/companies/${id}`, data),
    delete: (id: string) => apiClient.delete(`/companies/${id}`),
  },

  // CRM
  crm: {
    contacts: {
      list: (params?: any) => apiClient.get('/crm/contacts', { params }),
      get: (id: string) => apiClient.get(`/crm/contacts/${id}`),
      create: (data: any) => apiClient.post('/crm/contacts', data),
      update: (id: string, data: any) => apiClient.put(`/crm/contacts/${id}`, data),
      delete: (id: string) => apiClient.delete(`/crm/contacts/${id}`),
    },
    deals: {
      list: (params?: any) => apiClient.get('/crm/deals', { params }),
      get: (id: string) => apiClient.get(`/crm/deals/${id}`),
      create: (data: any) => apiClient.post('/crm/deals', data),
      update: (id: string, data: any) => apiClient.put(`/crm/deals/${id}`, data),
      delete: (id: string) => apiClient.delete(`/crm/deals/${id}`),
    },
  },

  // Finance
  finance: {
    transactions: {
      list: (params?: any) => apiClient.get('/finance/transactions', { params }),
      get: (id: string) => apiClient.get(`/finance/transactions/${id}`),
      create: (data: any) => apiClient.post('/finance/transactions', data),
      delete: (id: string) => apiClient.delete(`/finance/transactions/${id}`),
    },
    invoices: {
      list: (params?: any) => apiClient.get('/finance/invoices', { params }),
      get: (id: string) => apiClient.get(`/finance/invoices/${id}`),
      create: (data: any) => apiClient.post('/finance/invoices', data),
      update: (id: string, data: any) => apiClient.put(`/finance/invoices/${id}`, data),
    },
    reports: {
      get: (type: string, params?: any) => apiClient.get(`/finance/reports/${type}`, { params }),
    },
  },

  // Users
  users: {
    list: () => apiClient.get('/users/'),
    get: (id: string) => apiClient.get(`/users/${id}`),
    create: (data: any) => apiClient.post('/users/', data),
    update: (id: string, data: any) => apiClient.put(`/users/${id}`, data),
    delete: (id: string) => apiClient.delete(`/users/${id}`),
  },

  // Cache
  cache: {
    clear: () => apiClient.post('/cache/clear'),
    clearKey: (key: string) => apiClient.delete(`/cache/${key}`),
    getStats: () => apiClient.get('/cache/stats'),
  },

  // HITL Tasks
  tasks: {
    list: (params?: any) => apiClient.get('/hitl/tasks', { params }),
    get: (id: string) => apiClient.get(`/hitl/tasks/${id}`),
    create: (data: any) => apiClient.post('/hitl/tasks', data),
    update: (id: string, data: any) => apiClient.put(`/hitl/tasks/${id}`, data),
    approve: (id: string, feedback?: string) =>
      apiClient.post(`/hitl/tasks/${id}/approve`, { feedback }),
    reject: (id: string, reason: string) =>
      apiClient.post(`/hitl/tasks/${id}/reject`, { reason }),
    requestChanges: (id: string, changes: string) =>
      apiClient.post(`/hitl/tasks/${id}/request-changes`, { changes }),
    delete: (id: string) => apiClient.delete(`/hitl/tasks/${id}`),
  },

  // AI Agents
  agents: {
    list: () => apiClient.get('/agents'),
    get: (id: string) => apiClient.get(`/agents/${id}`),
    configure: (id: string, config: any) => apiClient.put(`/agents/${id}/config`, config),
    enable: (id: string) => apiClient.post(`/agents/${id}/enable`),
    disable: (id: string) => apiClient.post(`/agents/${id}/disable`),
    status: (id: string) => apiClient.get(`/agents/${id}/status`),
    history: (id: string, params?: any) => apiClient.get(`/agents/${id}/history`, { params }),
  },

  // Agent Tasks
  agentTasks: {
    trigger: (agentId: string, taskType: string, data: any) =>
      apiClient.post(`/agents/${agentId}/tasks`, { task_type: taskType, data }),
    getResult: (agentId: string, taskId: string) =>
      apiClient.get(`/agents/${agentId}/tasks/${taskId}`),
  },

  // Notifications
  notifications: {
    list: (params?: any) => apiClient.get('/notifications', { params }),
    get: (id: string) => apiClient.get(`/notifications/${id}`),
    markRead: (id: string) => apiClient.put(`/notifications/${id}/read`),
    markAllRead: () => apiClient.put('/notifications/read-all'),
    delete: (id: string) => apiClient.delete(`/notifications/${id}`),
    getUnreadCount: () => apiClient.get('/notifications/unread-count'),
  },

  // Analytics
  analytics: {
    overview: () => apiClient.get('/analytics/overview'),
    supplyChain: (params?: any) => apiClient.get('/analytics/supply-chain', { params }),
    performance: (params?: any) => apiClient.get('/analytics/performance', { params }),
    forecasts: (params?: any) => apiClient.get('/analytics/forecasts', { params }),
    risks: () => apiClient.get('/analytics/risks'),
  },

  // Company/User Settings
  company: {
    get: () => apiClient.get('/company'),
    update: (data: any) => apiClient.put('/company', data),
    members: () => apiClient.get('/company/members'),
    inviteMember: (email: string, role: string) =>
      apiClient.post('/company/members/invite', { email, role }),
  },

  // User Profile
  user: {
    get: () => apiClient.get('/user/profile'),
    update: (data: any) => apiClient.put('/user/profile', data),
    changePassword: (currentPassword: string, newPassword: string) =>
      apiClient.put('/user/password', { current_password: currentPassword, new_password: newPassword }),
    preferences: () => apiClient.get('/user/preferences'),
    updatePreferences: (data: any) => apiClient.put('/user/preferences', data),
  },
};

export default api;
